import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getAuthService } from "../firebase/services";
import { Loader2, ArrowLeft, Eye, EyeOff, Lock, Mail } from "lucide-react";
import rfaLogo from "../assets/rfa logo.webp";
import { usePersistentState } from "../hooks/usePersistentState";

const Login = () => {
  const navigate = useNavigate();
  const [savedEmail, setSavedEmail] = usePersistentState(
    "rfa_login_email_v1",
    "",
  );
  const [formData, setFormData] = useState({ email: savedEmail, password: "" });
  const [error, setError] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // NEW: State to track password visibility
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const next = { ...formData, [e.target.name]: e.target.value };
    setFormData(next);
    if (e.target.name === "email") setSavedEmail(next.email);
    setError("");
    setResetMessage("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setResetMessage("");
    setLoading(true);

    try {
      const auth = await getAuthService();
      const { signInWithEmailAndPassword } = await import("firebase/auth");
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      navigate("/dashboard");
    } catch (err) {
      if (
        err.code === "auth/invalid-credential" ||
        err.code === "auth/user-not-found"
      ) {
        setError("Invalid email or password. Please check your welcome email.");
      } else {
        setError("Failed to log in. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    setResetMessage("");

    if (!formData.email) {
      setError("Please enter your email address first to reset your password.");
      return;
    }

    try {
      const auth = await getAuthService();
      const { sendPasswordResetEmail } = await import("firebase/auth");
      await sendPasswordResetEmail(auth, formData.email);
      setResetMessage("Password reset email sent! Please check your inbox.");
    } catch (err) {
      if (err.code === "auth/user-not-found") {
        setError("No account found with that email address.");
      } else {
        setError("Failed to send reset email. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-[#7D192A] via-[#5A121E] to-[#1A1C25] px-4 py-12 flex flex-col items-center justify-center">
      <Helmet>
        <title>Login | Reflection Futbol Academy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-md w-full">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/80 hover:text-[#F4B400] font-inter font-bold text-sm mb-6 transition-colors group"
        >
          <ArrowLeft
            size={18}
            className="transition-transform group-hover:-translate-x-1"
          />
          BACK TO HOME
        </Link>

        <div className="bg-white/80 backdrop-blur-xl p-8 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.25)] border border-white/50">
          <div className="flex justify-center mb-6">
            <img
              src={rfaLogo}
              alt="Academy Logo"
              width={80}
              height={80}
              className="w-20 h-20 object-contain drop-shadow-md"
            />
          </div>

          <h2 className="text-2xl font-black text-center font-inter text-[#7D192A] mb-2 uppercase tracking-tight">
            Member Portal
          </h2>
          <p className="text-center text-sm text-gray-700 font-inter mb-8">
            Enter credentials from your email.
          </p>

          {error && (
            <div className="bg-red-50/90 text-red-600 p-3 rounded-lg text-sm font-inter mb-6 border border-red-200 text-center backdrop-blur-sm">
              {error}
            </div>
          )}

          {resetMessage && (
            <div className="bg-green-50/90 text-green-700 p-3 rounded-lg text-sm font-inter mb-6 border border-green-200 text-center backdrop-blur-sm">
              {resetMessage}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="text-[11px] font-inter font-extrabold text-[#1A1C25] uppercase tracking-widest block mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-white/50 border border-white/40 rounded-lg focus:bg-white focus:border-[#F4B400] outline-none transition-all font-inter shadow-sm"
                  placeholder="registered email"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-[11px] font-inter font-extrabold text-[#1A1C25] uppercase tracking-widest block">
                  Password
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-[11px] font-bold text-[#7D192A] hover:text-[#F4B400] transition-colors"
                >
                  Forgot?
                </button>
              </div>

              <div className="relative">
                <Lock
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 bg-white/50 border border-white/40 rounded-lg focus:bg-white focus:border-[#F4B400] outline-none transition-all font-inter shadow-sm"
                  placeholder="••••••••"
                />

                {/* NEW: Toggle Button */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#7D192A] transition-colors p-1"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 mt-6 bg-[#F4B400] text-[#1A1C25] font-black uppercase tracking-widest rounded-full shadow-lg hover:bg-[#e0a600] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {loading ? (
                <Loader2 className="animate-spin text-[#1A1C25]" size={20} />
              ) : (
                "Login"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
